import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { leadsApi, Lead } from '../api/leads';
import { adminApi } from '../api/admin';
import { accountsApi } from '../api/accounts';
import { contactsApi } from '../api/contacts';
import { leadContactsApi, LeadContact } from '../api/lead-contacts';
import { tagsApi } from '../api/tags';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import TagSelector from '../components/TagSelector';
import TagDisplay from '../components/TagDisplay';
import CustomFieldsRenderer from '../components/CustomFieldsRenderer';
import { customFieldsApi } from '../api/customFields';
import { formatCurrency, Currency } from '../utils/currencyUtils';
import { parseLeadValue } from '../utils/leadUtils';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, EyeIcon, ArrowTopRightOnSquareIcon, XMarkIcon, FunnelIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

// Service line options for multi-select (stored as comma-separated string)
export const SERVICE_LINE_OPTIONS = [
  'NeuroRank - Full Service',
  'NeuroRank - Tool + Advisory',
  'Channel Command',
  'Website',
  'Social Media Strategy',
  'Creative & Video Production',
  'GTM Strategy',
  'B2B/Linkedin Marketing',
  'Martech & CRO',
  'Branding',
  'Performance Marketing',
  'Influencer Marketing',
  'Others',
];

// Lead source options for dropdown
export const LEAD_SOURCE_OPTIONS = [
  'Inbound - Form Fills',
  'Inbound - Direct Email',
  'Referral - Internal',
  'Referral - Client/External',
  'Outbound - Email Drip',
  'Outbound - Paid Media',
  'Outbound - Webinar',
  'Existing Client',
];

const stages = [
  { id: 'new', name: 'New', color: 'bg-slate-500', borderColor: 'border-slate-300', tooltip: 'Fresh lead - not yet contacted' },
  { id: 'contacted', name: 'Contacted', color: 'bg-blue-500', borderColor: 'border-blue-300', tooltip: 'Initial contact made' },
  { id: 'first_meeting', name: 'First Meeting', color: 'bg-cyan-500', borderColor: 'border-cyan-300', tooltip: 'First meeting completed' },
  { id: 'second_meeting', name: 'Second Meeting', color: 'bg-teal-500', borderColor: 'border-teal-300', tooltip: 'Second meeting completed' },
  { id: 'qualified', name: 'Qualified', color: 'bg-emerald-500', borderColor: 'border-emerald-300', tooltip: 'Lead qualified - set A/B/C category' },
  { id: 'pitch_presented', name: 'Pitch Presented', color: 'bg-amber-500', borderColor: 'border-amber-300', tooltip: 'Pitch/presentation delivered' },
  { id: 'sow_in_progress', name: 'SOW in Progress', color: 'bg-orange-500', borderColor: 'border-orange-300', tooltip: 'Preparing Statement of Work' },
  { id: 'sow_sent', name: 'SOW Sent', color: 'bg-pink-500', borderColor: 'border-pink-300', tooltip: 'Proposal sent to client' },
  { id: 'clarification_call', name: 'Clarification Call', color: 'bg-fuchsia-500', borderColor: 'border-fuchsia-300', tooltip: 'Addressing client questions' },
  { id: 'roi_proof_call', name: 'ROI / Proof Call', color: 'bg-purple-500', borderColor: 'border-purple-300', tooltip: 'ROI discussion completed' },
  { id: 'decision_stage', name: 'Decision Stage', color: 'bg-violet-500', borderColor: 'border-violet-300', tooltip: 'Client making final decision' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-indigo-500', borderColor: 'border-indigo-300', tooltip: 'Negotiating terms' },
  { id: 'won', name: 'Won', color: 'bg-green-500', borderColor: 'border-green-300', tooltip: 'Deal closed successfully! 🎉' },
  { id: 'lost', name: 'Lost', color: 'bg-red-500', borderColor: 'border-red-300', tooltip: 'Deal lost - record reason' },
];

const Leads: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    stage: '' as string,
    industry: '' as string,
    category: [] as ('A' | 'B' | 'C')[],
    account_id: '' as string,
    lead_source: '' as string,
    geography: '' as string,
    service_line: '' as string,
    brand_type: '' as string,
    owner_ids: [] as string[],
  });
  const [formData, setFormData] = useState({
    name: '',
    lead_name: '', // Keep for backward compatibility
    first_name: '',
    last_name: '',
    stage: 'new',
    value: '',
    deal_value: '',
    currency: 'INR' as 'USD' | 'INR', // Always INR - currency selector removed
    probability: '',
    expected_close_date: '',
    account_id: '',
    primary_contact_id: '',
    deal_owner_user_id: '',
    service_line: '',
    industry: '',
    brand_type: '',
    geography: '',
    source: '',
    lead_source: '' as string,
    date_created: '',
    last_contacted_at: '',
    category: '' as '' | 'A' | 'B' | 'C',
    description: '',
    notes: '',
    problem_statement: '',
    solution_positioning: '',
    competitor: '',
    urgency_score: '',
    next_action: '',
    next_action_date: '',
    close_plan_id: '',
    lost_reason: '',
    brand: '',
    decision_maker: '',
    decision_maker_contact_id: '',
    decision_maker_level: '',
    influencers: '',
    reason: '',
    won_date: '',
    lost_date: '',
    contact_email: '',
    contact_phone: '',
  });
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [leadContacts, setLeadContacts] = useState<LeadContact[]>([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [newAccountData, setNewAccountData] = useState({
    name: '',
    industry: '',
    website: '',
    phone: '',
    address: '',
  });
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const contactDropdownRef = useRef<HTMLDivElement>(null);
  const [serviceLineDropdownOpen, setServiceLineDropdownOpen] = useState(false);
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerDropdownPosition, setOwnerDropdownPosition] = useState({ top: 0, left: 0 });
  const ownerDropdownRef = useRef<HTMLDivElement>(null);
  const ownerDropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [newContactData, setNewContactData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    title: '',
  });
  const pipelineScrollRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const { data: leads = [], isLoading, error } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
    enabled: !!user, // Only fetch if user is authenticated
    retry: 1,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
    enabled: !!user,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.getAll,
    enabled: !!user,
  });

  // Handle errors separately
  React.useEffect(() => {
    if (error) {
      console.error('Leads query error:', error);
      if ((error as any)?.response) {
        console.error('Response status:', (error as any).response.status);
        console.error('Response data:', (error as any).response.data);
      }
      if ((error as any)?.request) {
        console.error('Request made but no response:', (error as any).request);
      }
      if ((error as any)?.message) {
        console.error('Error message:', (error as any).message);
      }
    }
  }, [error]);

  const createMutation = useMutation({
    mutationFn: leadsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowModal(false);
      resetForm();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to create lead. Please check all required fields.';
      alert(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Lead> }) =>
      leadsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowModal(false);
      setEditingLead(null);
      resetForm();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to update lead. Please check the requirements.';
      alert(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: leadsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const createAccountMutation = useMutation({
    mutationFn: accountsApi.create,
    onSuccess: (newAccount) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setFormData({ ...formData, account_id: String(newAccount.id) });
      setShowAccountModal(false);
      setNewAccountData({ name: '', industry: '', website: '', phone: '', address: '' });
    },
  });

  const createContactMutation = useMutation({
    mutationFn: contactsApi.create,
    onSuccess: (newContact) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Add to selected contacts instead of primary_contact_id
      if (!selectedContactIds.includes(newContact.id)) {
        setSelectedContactIds([...selectedContactIds, newContact.id]);
      }
      setShowContactModal(false);
      setNewContactData({ first_name: '', last_name: '', email: '', phone: '', title: '' });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      lead_name: '', // Keep for backward compatibility
      first_name: '',
      last_name: '',
      stage: 'new',
      value: '',
      deal_value: '',
      currency: 'INR' as 'USD' | 'INR', // Always INR - currency selector removed
      probability: '',
      expected_close_date: '',
      account_id: '',
      primary_contact_id: '',
      deal_owner_user_id: '',
      service_line: '',
      industry: '',
      brand_type: '',
      geography: '',
      source: '',
      lead_source: '' as string,
      date_created: '',
      last_contacted_at: '',
      category: '' as '' | 'A' | 'B' | 'C',
      description: '',
      notes: '',
      problem_statement: '',
      solution_positioning: '',
      competitor: '',
      urgency_score: '',
      next_action: '',
      next_action_date: '',
      close_plan_id: '',
      lost_reason: '',
      brand: '',
      decision_maker: '',
      decision_maker_contact_id: '',
      decision_maker_level: '',
      influencers: '',
      reason: '',
      won_date: '',
      lost_date: '',
      contact_email: '',
      contact_phone: '',
    });
    setSelectedTagIds([]);
    setCustomFieldValues({});
    setSelectedContactIds([]);
    setLeadContacts([]);
    setServiceLineDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Helper to parse number, return null if empty. Round to 2 decimals to avoid float display (31000 → 30999)
    const parseNumber = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return null;
      return Math.round(parsed * 100) / 100;
    };
    
    // Helper to parse integer, return null if empty
    const parseIntSafe = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    };
    
    // Helper to clean string - return null if empty, otherwise return trimmed string
    const cleanString = (value: string): string | null => {
      if (!value || value.trim() === '') return null;
      return value.trim();
    };
    
    // Build leadData - first field is Company/Brand name only (not person name)
    const companyOrBrandName = formData.name.trim();
    const leadData: any = {
      name: companyOrBrandName,
      brand: companyOrBrandName,
      first_name: formData.first_name?.trim() || '',
      last_name: formData.last_name?.trim() || '',
      lead_name: [formData.first_name?.trim(), formData.last_name?.trim()].filter(Boolean).join(' ').trim() || companyOrBrandName,
      stage: formData.stage,
      currency: formData.currency,
    };

    // Send value as string so JSON/backend never loses precision (e.g. 55999999)
    const valueStr = (formData.value || '').trim().replace(/,/g, '');
    leadData.value = valueStr !== '' ? valueStr : '0';

    // Include deal_value as string if provided
    if (formData.deal_value !== '') {
      const dealStr = (formData.deal_value || '').trim().replace(/,/g, '');
      if (dealStr !== '') leadData.deal_value = dealStr;
    } else if (editingLead) {
      leadData.deal_value = null;
    }

    // Always include probability - parse it or let backend calculate from stage
    const parsedProbability = parseIntSafe(formData.probability);
    if (parsedProbability !== null) {
      leadData.probability = parsedProbability;
    }
    // If probability is empty, backend will calculate from stage

    // Include owner_id
    if (user?.id) {
      leadData.owner_id = user.id;
    }

    // Include deal_owner_user_id
    if (formData.deal_owner_user_id) {
      const parsedDealOwner = parseIntSafe(formData.deal_owner_user_id);
      if (parsedDealOwner !== null) {
        leadData.deal_owner_user_id = parsedDealOwner;
      }
    } else if (editingLead) {
      leadData.deal_owner_user_id = user?.id || null;
    }

    // Include all text fields (even if empty, to allow clearing)
    leadData.expected_close_date = cleanString(formData.expected_close_date);
    leadData.service_line = cleanString(formData.service_line);
    leadData.industry = cleanString(formData.industry);
    leadData.brand_type = cleanString(formData.brand_type);
    leadData.geography = cleanString(formData.geography);
    leadData.lead_source = formData.lead_source || null;
    leadData.date_created = cleanString(formData.date_created);
    leadData.last_contacted_at = cleanString(formData.last_contacted_at);
    leadData.category = formData.category || null;
    leadData.description = cleanString(formData.description);
    leadData.notes = cleanString(formData.notes);
    leadData.problem_statement = cleanString(formData.problem_statement);
    leadData.solution_positioning = cleanString(formData.solution_positioning);
    leadData.competitor = cleanString(formData.competitor);
    leadData.next_action = cleanString(formData.next_action);
    leadData.next_action_date = cleanString(formData.next_action_date);
    // Brand / Company name: single field in form drives both name and brand so edits from Leads persist
    leadData.brand = companyOrBrandName;
    leadData.decision_maker = cleanString(formData.decision_maker);
    leadData.decision_maker_level = cleanString(formData.decision_maker_level);
    leadData.influencers = cleanString(formData.influencers);
    leadData.reason = cleanString(formData.reason);

    // Include numeric fields
    const parsedUrgency = parseIntSafe(formData.urgency_score);
    if (parsedUrgency !== null) {
      leadData.urgency_score = parsedUrgency;
    }

    // Include contact IDs
    if (formData.decision_maker_contact_id) {
      const parsedContactId = parseIntSafe(formData.decision_maker_contact_id);
      if (parsedContactId !== null) {
        leadData.decision_maker_contact_id = parsedContactId;
      }
    } else if (editingLead) {
      leadData.decision_maker_contact_id = null;
    }

    // Stage-specific fields
    if (formData.stage === 'lost') {
      leadData.lost_reason = formData.lost_reason?.trim() || null;
      leadData.lost_date = cleanString(formData.lost_date);
    }
    if (formData.stage === 'won') {
      leadData.won_date = cleanString(formData.won_date);
    }
    
    // Handle account_id - include it even if empty (to allow clearing)
    if (formData.account_id && formData.account_id !== '') {
      const parsedAccountId = parseIntSafe(formData.account_id);
      if (parsedAccountId !== null) {
        leadData.account_id = parsedAccountId;
      }
    } else if (editingLead) {
      // Allow clearing account_id when editing
      leadData.account_id = null;
    }
    
    // Handle primary_contact_id - include it even if empty (to allow clearing)
    if (formData.primary_contact_id && formData.primary_contact_id !== '') {
      const parsedPrimaryContactId = parseIntSafe(formData.primary_contact_id);
      if (parsedPrimaryContactId !== null) {
        leadData.primary_contact_id = parsedPrimaryContactId;
      }
    } else if (editingLead) {
      // Allow clearing primary_contact_id when editing
      leadData.primary_contact_id = null;
    }

    if (editingLead) {
      // Debug: Log the data being sent
      console.log('🔍 Frontend - Updating lead:', {
        id: editingLead.id,
        leadData,
        fieldsCount: Object.keys(leadData).length,
        value: leadData.value,
        deal_value: leadData.deal_value,
        probability: leadData.probability,
      });
      
      updateMutation.mutate(
        { id: editingLead.id, data: leadData },
        {
          onSuccess: async () => {
            // Invalidate all lead-related queries to ensure fresh data
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead', editingLead.id] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            // Update tags after lead is updated
            try {
              await tagsApi.setTags('lead', editingLead.id, selectedTagIds);
              queryClient.invalidateQueries({ queryKey: ['tags', 'lead', editingLead.id] });
            } catch (error) {
              console.error('Error updating tags:', error);
            }
            // Update custom field values
            try {
              if (Object.keys(customFieldValues).length > 0) {
                await customFieldsApi.setValues('lead', editingLead.id, customFieldValues);
              }
            } catch (error) {
              console.error('Error updating custom fields:', error);
            }
            // Sync primary contact email/phone from lead form
            try {
              const parsedPrimaryId = formData.primary_contact_id
                ? parseIntSafe(formData.primary_contact_id)
                : null;
              const targetContactId =
                parsedPrimaryId ??
                editingLead.primary_contact_id ??
                (selectedContactIds.length > 0 ? selectedContactIds[0] : null);

              if (targetContactId) {
                await contactsApi.update(targetContactId, {
                  email: formData.contact_email || undefined,
                  phone: formData.contact_phone || undefined,
                });
                queryClient.invalidateQueries({ queryKey: ['contacts'] });
                queryClient.invalidateQueries({ queryKey: ['lead-contacts', editingLead.id] });
              }
            } catch (error) {
              console.error('Error syncing contact email/phone from lead:', error);
            }

            // Sync brand/company name to linked account when edited from Leads (same as editing from Account)
            const accountId = formData.account_id ? parseIntSafe(formData.account_id) : editingLead.account_id;
            if (accountId && (formData.name || '').trim()) {
              try {
                await accountsApi.update(accountId, { name: (formData.name || '').trim() });
                queryClient.invalidateQueries({ queryKey: ['accounts'] });
              } catch (error) {
                console.error('Error syncing brand name to account:', error);
              }
            }

            // Update lead contacts
            try {
              // Get current contacts
              const currentContacts = await leadContactsApi.getByLeadId(editingLead.id);
              const currentContactIds = currentContacts.map(lc => lc.contact_id);
              
              // Add new contacts
              for (const contactId of selectedContactIds) {
                if (!currentContactIds.includes(contactId)) {
                  // Set first contact as primary if no primary exists
                  const hasPrimary = currentContacts.some(lc => lc.is_primary);
                  await leadContactsApi.addContact(editingLead.id, contactId, undefined, !hasPrimary && selectedContactIds.indexOf(contactId) === 0);
                }
              }
              
              // Remove contacts that are no longer selected
              for (const currentContact of currentContacts) {
                if (!selectedContactIds.includes(currentContact.contact_id)) {
                  await leadContactsApi.removeContact(editingLead.id, currentContact.contact_id);
                }
              }
              
              queryClient.invalidateQueries({ queryKey: ['lead-contacts', editingLead.id] });
            } catch (error) {
              console.error('Error updating lead contacts:', error);
            }
          },
        }
      );
    } else {
      createMutation.mutate(leadData, {
        onSuccess: async (newLead) => {
          // Set tags after lead is created
          if (selectedTagIds.length > 0) {
            try {
              await tagsApi.setTags('lead', newLead.id, selectedTagIds);
              queryClient.invalidateQueries({ queryKey: ['tags', 'lead', newLead.id] });
            } catch (error) {
              console.error('Error setting tags:', error);
            }
          }
          // Set custom field values
          try {
            if (Object.keys(customFieldValues).length > 0) {
              await customFieldsApi.setValues('lead', newLead.id, customFieldValues);
            }
          } catch (error) {
            console.error('Error setting custom fields:', error);
          }
          // Sync primary contact email/phone from lead form for newly created lead
          try {
            const targetContactId =
              newLead.primary_contact_id ??
              (selectedContactIds.length > 0 ? selectedContactIds[0] : null);

            if (targetContactId) {
              await contactsApi.update(targetContactId, {
                email: formData.contact_email || undefined,
                phone: formData.contact_phone || undefined,
              });
              queryClient.invalidateQueries({ queryKey: ['contacts'] });
              queryClient.invalidateQueries({ queryKey: ['lead-contacts', newLead.id] });
            }
          } catch (error) {
            console.error('Error syncing contact email/phone for new lead:', error);
          }

          // Add contacts to the new lead
          try {
            for (let i = 0; i < selectedContactIds.length; i++) {
              const contactId = selectedContactIds[i];
              // Set first contact as primary
              await leadContactsApi.addContact(newLead.id, contactId, undefined, i === 0);
            }
            queryClient.invalidateQueries({ queryKey: ['lead-contacts', newLead.id] });
          } catch (error) {
            console.error('Error adding contacts to lead:', error);
          }
        },
      });
    }
  };

  const handleEdit = async (lead: Lead) => {
    setEditingLead(lead);
    
    // Load existing contacts for this lead
    try {
      const contacts = await leadContactsApi.getByLeadId(lead.id);
      setLeadContacts(contacts);
      setSelectedContactIds(contacts.map(lc => lc.contact_id));
    } catch (error) {
      console.error('Error loading lead contacts:', error);
      setLeadContacts([]);
      setSelectedContactIds([]);
    }
    
    // Helper function to format date for date input
    const formatDateForInput = (dateValue: string | Date | undefined): string => {
      if (!dateValue) return '';
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };
    
    // Helper function to format datetime for datetime-local input
    const formatDateTimeForInput = (dateValue: string | Date | undefined): string => {
      if (!dateValue) return '';
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      if (isNaN(date.getTime())) return '';
      // Format as YYYY-MM-DDTHH:mm for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    // Helper to split name into first and last name
    const splitName = (fullName: string): { first: string; last: string } => {
      if (!fullName) return { first: '', last: '' };
      const parts = fullName.trim().split(' ');
      return {
        first: parts[0] || '',
        last: parts.slice(1).join(' ') || '',
      };
    };

    // Get first_name and last_name, with backward compatibility
    const nameParts = lead.first_name && lead.last_name
      ? { first: lead.first_name, last: lead.last_name }
      : lead.first_name
      ? { first: lead.first_name, last: lead.last_name || '' }
      : splitName(lead.lead_name || lead.name || '');

    const primaryContact = contacts.find(c => c.id === lead.primary_contact_id);

    setFormData({
      name: lead.brand || lead.name || '',
      lead_name: lead.lead_name || (lead.first_name && lead.last_name ? `${lead.first_name} ${lead.last_name}`.trim() : lead.name) || '',
      first_name: nameParts.first,
      last_name: nameParts.last,
      stage: lead.stage || 'new',
      value: lead.value !== undefined && lead.value !== null ? String(lead.value).replace(/,/g, '') : '',
      deal_value: lead.deal_value !== undefined && lead.deal_value !== null ? String(lead.deal_value).replace(/,/g, '') : '',
      currency: (lead.currency || 'INR') as 'USD' | 'INR',
      probability: lead.probability !== undefined && lead.probability !== null ? String(lead.probability) : '',
      expected_close_date: formatDateForInput(lead.expected_close_date),
      account_id: lead.account_id !== undefined && lead.account_id !== null ? String(lead.account_id) : '',
      primary_contact_id: lead.primary_contact_id !== undefined && lead.primary_contact_id !== null ? String(lead.primary_contact_id) : '',
      deal_owner_user_id: lead.deal_owner_user_id !== undefined && lead.deal_owner_user_id !== null ? String(lead.deal_owner_user_id) : '',
      service_line: lead.service_line || '',
      industry: lead.industry || '',
      brand_type: lead.brand_type || '',
      geography: lead.geography || '',
      source: lead.source || '',
      lead_source: lead.lead_source || '',
      date_created: formatDateForInput(lead.date_created),
      last_contacted_at: formatDateTimeForInput(lead.last_contacted_at),
      category: (lead.category || '') as '' | 'A' | 'B' | 'C',
      description: lead.description || '',
      notes: lead.notes || '',
      problem_statement: lead.problem_statement || '',
      solution_positioning: lead.solution_positioning || '',
      competitor: lead.competitor || '',
      urgency_score: lead.urgency_score !== undefined && lead.urgency_score !== null ? String(lead.urgency_score) : '',
      next_action: lead.next_action || '',
      next_action_date: formatDateForInput(lead.next_action_date),
      close_plan_id: lead.close_plan_id !== undefined && lead.close_plan_id !== null ? String(lead.close_plan_id) : '',
      lost_reason: lead.lost_reason || '',
      brand: lead.brand || lead.name || '',
      decision_maker: lead.decision_maker || '',
      decision_maker_contact_id: lead.decision_maker_contact_id !== undefined && lead.decision_maker_contact_id !== null ? String(lead.decision_maker_contact_id) : '',
      decision_maker_level: lead.decision_maker_level || '',
      influencers: lead.influencers || '',
      reason: lead.reason || '',
      won_date: formatDateForInput(lead.won_date),
      lost_date: formatDateForInput(lead.lost_date),
      contact_email: primaryContact?.email || '',
      contact_phone: primaryContact?.phone || '',
    });
    // Load tags for this lead
    try {
      const tags = await tagsApi.getByEntity('lead', lead.id);
      setSelectedTagIds(tags.map(t => t.id));
    } catch (error) {
      console.error('Error loading tags:', error);
      setSelectedTagIds([]);
    }
    // Load custom field values
    try {
      const customValues = await customFieldsApi.getValues('lead', lead.id);
      // Backend returns object with field_name as key and {value, type, label} as value
      const values: Record<string, any> = {};
      Object.keys(customValues).forEach(key => {
        values[key] = customValues[key]?.value || customValues[key];
      });
      setCustomFieldValues(values);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      setCustomFieldValues({});
    }
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  // Helper: contact name from lead (uses primary contact from API when available)
  const getContactName = (lead: Lead): string => {
    const first = lead.primary_contact_first_name ?? lead.first_name;
    const last = lead.primary_contact_last_name ?? lead.last_name;
    if (first && last) return `${first} ${last}`.trim();
    if (first) return first;
    return lead.lead_name || '';
  };

  const filteredLeads = leads.filter(lead => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const brand = (lead.brand || lead.name || '').toLowerCase();
      const contactName = getContactName(lead).toLowerCase();
      const companyName = (lead.name || '').toLowerCase();
      const industry = (lead.industry || '').toLowerCase();
      
      if (!brand.includes(searchLower) && 
          !contactName.includes(searchLower) && 
          !companyName.includes(searchLower) &&
          !industry.includes(searchLower)) {
        return false;
      }
    }

    // Stage filter
    if (filters.stage && lead.stage !== filters.stage) {
      return false;
    }

    // Industry filter
    if (filters.industry && lead.industry?.toLowerCase() !== filters.industry.toLowerCase()) {
      return false;
    }

    // Category filter (multiple)
    if (filters.category.length > 0) {
      if (!lead.category || !filters.category.includes(lead.category as 'A' | 'B' | 'C')) return false;
    }

    // Account filter
    if (filters.account_id && lead.account_id?.toString() !== filters.account_id) {
      return false;
    }

    // Lead source filter
    if (filters.lead_source && lead.lead_source !== filters.lead_source) {
      return false;
    }

    // Geography filter
    if (filters.geography && lead.geography?.toLowerCase() !== filters.geography.toLowerCase()) {
      return false;
    }

    // Service line filter (lead can have multiple comma-separated; match if any equals filter)
    if (filters.service_line) {
      const leadLines = (lead.service_line || '').split(',').map(s => s.trim()).filter(Boolean);
      const match = leadLines.some(s => s.toLowerCase() === filters.service_line!.toLowerCase());
      if (!match) return false;
    }

    // Brand type filter
    if (filters.brand_type && lead.brand_type?.toLowerCase() !== filters.brand_type.toLowerCase()) {
      return false;
    }

    // Owner filter (for admin/superadmin/leadership) - multi-select
    if (filters.owner_ids.length > 0) {
      const ownerIdStr = lead.owner_id != null ? lead.owner_id.toString() : '';
      if (!ownerIdStr || !filters.owner_ids.includes(ownerIdStr)) {
        return false;
      }
    }

    return true;
  });

  // Get unique values for filter dropdowns (service line uses fixed options)
  const uniqueIndustries = Array.from(new Set(leads.map(l => l.industry).filter(Boolean))).sort();
  const uniqueGeographies = Array.from(new Set(leads.map(l => l.geography).filter(Boolean))).sort();
  const uniqueBrandTypes = Array.from(new Set(leads.map(l => l.brand_type).filter(Boolean))).sort();

  // Count active filters (category and owner_ids are arrays)
  const activeFilterCount = Object.entries(filters).filter(([k, v]) =>
    k === 'category' || k === 'owner_ids'
      ? (Array.isArray(v) && v.length > 0)
      : v !== ''
  ).length;

  const canFilterByOwner = !!user && ['superadmin', 'admin', 'leadership'].includes(user.role);
  const { data: teamUsers = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.getUsers(),
    enabled: canFilterByOwner,
  });
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      stage: '',
      industry: '',
      category: [],
      account_id: '',
      lead_source: '',
      geography: '',
      service_line: '',
      brand_type: '',
      owner_ids: [],
    });
    setSearchTerm('');
  };

  const leadsByStage = stages.map((stage) => ({
    ...stage,
    leads: filteredLeads.filter((lead) => lead.stage === stage.id),
  }));

  // Filter to only show stages with results when filters are active
  const visibleStages = activeFilterCount > 0
    ? leadsByStage.filter(({ leads }) => leads.length > 0)
    : leadsByStage;

  // Auto-scroll to first stage with results when filters change
  useEffect(() => {
    if (activeFilterCount > 0 && pipelineScrollRef.current && visibleStages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (pipelineScrollRef.current) {
          pipelineScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [activeFilterCount, visibleStages.length]);

  // Owner dropdown: position (fixed) and click-outside (portal is in document.body)
  useEffect(() => {
    const updatePosition = () => {
      if (ownerDropdownButtonRef.current) {
        const rect = ownerDropdownButtonRef.current.getBoundingClientRect();
        setOwnerDropdownPosition({ top: rect.bottom + 8, left: rect.left });
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ownerDropdownRef.current && !ownerDropdownRef.current.contains(target) &&
        ownerDropdownButtonRef.current && !ownerDropdownButtonRef.current.contains(target)
      ) {
        setOwnerDropdownOpen(false);
      }
    };
    if (ownerDropdownOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ownerDropdownOpen]);

  // Close contact dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(e.target as Node)) {
        setContactDropdownOpen(false);
      }
    };
    if (contactDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contactDropdownOpen]);

  // Service line options: curated list + any existing values on leads (old data)
  const allServiceLines = React.useMemo(() => {
    const dynamic = leads.flatMap((l) =>
      (l.service_line || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );
    return Array.from(new Set<string>([...SERVICE_LINE_OPTIONS, ...dynamic])).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [leads]);

  // All amounts in INR - sum lead values directly
  const totalValue = filteredLeads.reduce((sum, l) => sum + parseLeadValue(l.value), 0);

  if (isLoading) {
    return (
      <PremiumLayout title="Leads Pipeline">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-slate-800"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : (error as any)?.response?.data?.error || 'Error loading leads. Please try again.';
    
    return (
      <PremiumLayout title="Leads Pipeline">
        <div className="premium-card p-6 bg-red-50 border-red-200">
          <p className="text-red-800 font-medium mb-2">Error loading leads</p>
          <p className="text-red-600 text-sm">{errorMessage}</p>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['leads'] })}
            className="mt-4 btn-primary text-sm"
          >
            Retry
          </button>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Leads Pipeline"
      subtitle={`${filteredLeads.length} leads • ${formatCurrency(totalValue, 'INR', { compact: true })} total value`}
      actions={
        <button
          onClick={() => {
            resetForm();
            setEditingLead(null);
            setShowModal(true);
          }}
          className="btn-primary text-sm px-4 py-2"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Lead
        </button>
      }
    >
      <div className="space-y-6">
        {/* Search and Filter Bar */}
        <div className="premium-card p-4">
          <div className="flex items-center space-x-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by company/brand name, contact, or industry..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-premium pl-10"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                activeFilterCount > 0
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-white text-indigo-600 rounded-full px-2 py-0.5 text-xs font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="relative z-30 mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Stage Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Stage
                  </label>
                  <select
                    value={filters.stage}
                    onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Stages</option>
                    {stages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Industry Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Industry
                  </label>
                  <select
                    value={filters.industry}
                    onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Industries</option>
                    {uniqueIndustries.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category Filter (tags / multi-select) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(['A', 'B', 'C'] as const).map((cat) => {
                      const selected = filters.category.includes(cat);
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            const next = selected
                              ? filters.category.filter((c) => c !== cat)
                              : [...filters.category, cat];
                            setFilters({ ...filters, category: next });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selected
                              ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
                              : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                  {filters.category.length > 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      {filters.category.length} selected — click to toggle
                    </p>
                  )}
                </div>

                {/* Account Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account
                  </label>
                  <select
                    value={filters.account_id}
                    onChange={(e) => setFilters({ ...filters, account_id: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Accounts</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id.toString()}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Lead Source Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Lead Source
                  </label>
                  <select
                    value={filters.lead_source}
                    onChange={(e) => setFilters({ ...filters, lead_source: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Sources</option>
                    {LEAD_SOURCE_OPTIONS.map((source) => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Geography Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Geography
                  </label>
                  <select
                    value={filters.geography}
                    onChange={(e) => setFilters({ ...filters, geography: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Geographies</option>
                    {uniqueGeographies.map((geo) => (
                      <option key={geo} value={geo}>
                        {geo}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Service Line Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Line
                  </label>
                  <select
                    value={filters.service_line}
                    onChange={(e) => setFilters({ ...filters, service_line: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Service Lines</option>
                    {allServiceLines.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Brand Type Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Brand Type
                  </label>
                  <select
                    value={filters.brand_type}
                    onChange={(e) => setFilters({ ...filters, brand_type: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">All Brand Types</option>
                    {uniqueBrandTypes.map((brandType) => (
                      <option key={brandType} value={brandType}>
                        {brandType}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Owner Filter (admin / superadmin / leadership) - dropdown multi-select via portal */}
                {canFilterByOwner && (
                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Owner
                    </label>
                    <button
                      ref={ownerDropdownButtonRef}
                      type="button"
                      onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                      className="input-premium w-full text-left flex items-center justify-between gap-2 min-h-[42px] py-2"
                    >
                      <span className="flex-1 min-w-0 truncate text-gray-700">
                        {filters.owner_ids.length === 0
                          ? 'All owners'
                          : `${filters.owner_ids.length} selected`}
                      </span>
                      <ChevronDownIcon className={`h-5 w-5 text-gray-400 flex-shrink-0 ${ownerDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {ownerDropdownOpen && createPortal(
                      <div
                        ref={ownerDropdownRef}
                        className="fixed min-w-[280px] w-[min(320px,calc(100vw-16px))] bg-white rounded-lg border border-gray-200 shadow-2xl overflow-hidden flex flex-col max-h-80 z-[99999]"
                        style={{ top: ownerDropdownPosition.top, left: ownerDropdownPosition.left }}
                      >
                        <div className="p-2 border-b border-gray-200 bg-gray-50">
                          <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search owners..."
                              value={ownerSearchTerm}
                              onChange={(e) => setOwnerSearchTerm(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto flex-1 min-h-0">
                          {(() => {
                            const q = ownerSearchTerm.trim().toLowerCase();
                            const filtered = q
                              ? teamUsers.filter(
                                  (u) =>
                                    (u.name || '').toLowerCase().includes(q) ||
                                    (u.email || '').toLowerCase().includes(q)
                                )
                              : teamUsers;
                            if (filtered.length === 0) {
                              return (
                                <div className="p-4 text-center text-sm text-gray-500">
                                  No owners found
                                </div>
                              );
                            }
                            return (
                              <div className="p-1">
                                {filtered.map((u) => {
                                  const idStr = u.id.toString();
                                  const selected = filters.owner_ids.includes(idStr);
                                  return (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onClick={() => {
                                        const next = selected
                                          ? filters.owner_ids.filter((id) => id !== idStr)
                                          : [...filters.owner_ids, idStr];
                                        setFilters({ ...filters, owner_ids: next });
                                      }}
                                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                                        selected ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'
                                      }`}
                                    >
                                      <div
                                        className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                                          selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                                        }`}
                                      >
                                        {selected && <CheckIcon className="h-2.5 w-2.5 text-white" />}
                                      </div>
                                      <span className="flex-1 min-w-0 truncate font-medium text-gray-900">
                                        {u.name || u.email}
                                      </span>
                                      {u.email && u.name && (
                                        <span className="flex-shrink-0 text-xs text-gray-500 truncate max-w-[120px]">
                                          {u.email}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                        {filters.owner_ids.length > 0 && (
                          <div className="p-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                            <span className="text-xs text-gray-600">
                              {filters.owner_ids.length} selected
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setFilters({ ...filters, owner_ids: [] });
                              }}
                              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              Clear all
                            </button>
                          </div>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Filter Info Banner */}
        {activeFilterCount > 0 && (
          <div className="premium-card p-3 bg-indigo-50 border-indigo-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-900">
                  Showing {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''} in {visibleStages.length} stage{visibleStages.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={() => {
                  if (pipelineScrollRef.current) {
                    pipelineScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                  }
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-1"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                <span>Scroll to start</span>
              </button>
            </div>
          </div>
        )}

        {/* Clean Pipeline View - Vertical Stack on Mobile, Horizontal on Desktop */}
        <div className="space-y-4 lg:hidden">
          {/* Mobile: Vertical Stack */}
            {(activeFilterCount > 0 ? visibleStages : leadsByStage).map(({ id, name, color, borderColor, leads: stageLeads }) => {
            if (stageLeads.length === 0) return null;
            const stageValue = stageLeads.reduce((sum, l) => sum + (parseFloat(String(l.value)) || 0), 0);
            return (
              <div key={id} className="premium-card overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${color}`}></div>
                      <h3 className="font-bold text-gray-900">{name}</h3>
                      <span className="text-sm text-gray-500">({stageLeads.length})</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{formatCurrency(stageValue, 'INR', { compact: true })}</span>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  {stageLeads.map((lead) => (
                    <div 
                      key={lead.id} 
                      className="bg-white/80 backdrop-blur-sm p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          {(lead.account_name || lead.brand) ? (
                            <>
                              <h4 className="font-semibold text-gray-900 text-sm">🏷️ {lead.account_name || lead.brand}</h4>
                              <p className="text-xs text-gray-600 mt-0.5">Lead: {lead.name}</p>
                            </>
                          ) : (
                            <h4 className="font-semibold text-gray-900 text-sm">{lead.name}</h4>
                          )}
                          <TagDisplay entityType="lead" entityId={lead.id} className="mt-1.5" />
                        </div>
                        <div className="flex space-x-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/leads/${lead.id}`);
                            }}
                            className="p-1.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(lead);
                            }}
                            className="p-1.5 text-gray-400 hover:text-slate-700 hover:bg-gray-100 rounded transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(lead.id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Value</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(parseLeadValue(lead.value), 'INR')}</span>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-500">Probability</span>
                            <span className="font-semibold text-gray-900">{lead.probability}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-slate-600 h-1.5 rounded-full transition-all"
                              style={{ width: `${lead.probability}%` }}
                            />
                          </div>
                        </div>
                        {lead.expected_close_date && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Close Date</span>
                            <span className="text-gray-700">{new Date(lead.expected_close_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {lead.industry && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Industry</span>
                            <span className="text-gray-700">{lead.industry}</span>
                          </div>
                        )}
                        {lead.brand && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Brand</span>
                            <span className="text-gray-700">{lead.brand}</span>
                          </div>
                        )}
                        {lead.decision_maker && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Decision Maker</span>
                            <span className="text-gray-700">{lead.decision_maker}</span>
                          </div>
                        )}
                        {lead.service_line && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Service Line</span>
                            <span className="text-gray-700">{lead.service_line}</span>
                          </div>
                        )}
                        {lead.geography && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Geography</span>
                            <span className="text-gray-700">{lead.geography}</span>
                          </div>
                        )}
                        {lead.category && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Category</span>
                            <span className="text-gray-700 font-semibold">{lead.category}</span>
                          </div>
                        )}
                        {canFilterByOwner && (lead.owner_name != null || lead.owner_id) && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Owner</span>
                            <span className="text-gray-700 font-medium">{lead.owner_name || teamUsers.find(u => u.id === lead.owner_id)?.name || '—'}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads/${lead.id}`);
                          }}
                          className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition-colors text-sm font-medium"
                        >
                          <EyeIcon className="h-4 w-4" />
                          <span>View Details</span>
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: Horizontal Kanban */}
        <div className="hidden lg:block relative">
          {/* Scroll Navigation Buttons */}
          {activeFilterCount > 0 && visibleStages.length > 0 && (
            <>
              <button
                onClick={() => {
                  if (pipelineScrollRef.current) {
                    pipelineScrollRef.current.scrollBy({ left: -320, behavior: 'smooth' });
                  }
                }}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors border border-gray-200"
                title="Scroll left"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  if (pipelineScrollRef.current) {
                    pipelineScrollRef.current.scrollBy({ left: 320, behavior: 'smooth' });
                  }
                }}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors border border-gray-200"
                title="Scroll right"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </>
          )}
          <div 
            ref={pipelineScrollRef}
            className="overflow-x-auto pb-4 scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="inline-flex gap-4 min-w-full">
              {(activeFilterCount > 0 ? visibleStages : leadsByStage).map(({ id, name, color, borderColor, leads: stageLeads }) => {
              const stageValue = stageLeads.reduce((sum, l) => sum + parseLeadValue(l.value), 0);
              return (
                <div key={id} className="flex-shrink-0 w-80 premium-card flex flex-col">
                  {/* Stage Header */}
                  <div className="p-4 border-b border-gray-200 bg-gray-50/50 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${color}`}></div>
                        <h3 className="font-bold text-gray-900 text-sm">{name}</h3>
                      </div>
                      <span className="bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-bold text-gray-700 border border-gray-200">
                        {stageLeads.length}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 font-medium">{formatCurrency(stageValue, 'INR', { compact: true })}</p>
                  </div>

                  {/* Leads List */}
                  <div className="flex-1 p-3 space-y-2.5 overflow-y-auto max-h-[calc(100vh-280px)]">
                    {stageLeads.map((lead) => (
                      <div
                        key={lead.id}
                        className="bg-white/90 backdrop-blur-sm p-3.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <div className="flex items-start justify-between mb-2.5">
                          <div className="flex-1 min-w-0 pr-2">
                            {lead.brand ? (
                              <>
                                <h4 className="font-semibold text-gray-900 text-sm leading-tight">🏷️ {lead.brand}</h4>
                                <p className="text-xs text-gray-600 mt-0.5">Lead: {lead.name}</p>
                              </>
                            ) : (
                              <h4 className="font-semibold text-gray-900 text-sm leading-tight">{lead.name}</h4>
                            )}
                            <TagDisplay entityType="lead" entityId={lead.id} className="mt-1" />
                          </div>
                          <div className="flex space-x-1 flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/leads/${lead.id}`);
                              }}
                              className="p-1 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(lead);
                              }}
                              className="p-1 text-gray-400 hover:text-slate-700 hover:bg-gray-100 rounded transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(lead.id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-500">Value</span>
                            <span className="font-bold text-gray-900">{formatCurrency(parseLeadValue(lead.value), 'INR')}</span>
                          </div>
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-gray-500">Win %</span>
                              <span className="font-semibold text-gray-700">{lead.probability}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-slate-600 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${lead.probability}%` }}
                              />
                            </div>
                          </div>
                          {lead.expected_close_date && (
                            <div className="flex justify-between items-center pt-1">
                              <span className="text-gray-500">Close</span>
                              <span className="text-gray-700 font-medium">{new Date(lead.expected_close_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {lead.industry && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Industry</span>
                              <span className="text-gray-700 font-medium">{lead.industry}</span>
                            </div>
                          )}
                          {lead.brand && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Brand</span>
                              <span className="text-gray-700 font-medium">{lead.brand}</span>
                            </div>
                          )}
                          {lead.decision_maker && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">DM</span>
                              <span className="text-gray-700 font-medium">{lead.decision_maker}</span>
                            </div>
                          )}
                          {lead.service_line && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Service</span>
                              <span className="text-gray-700 font-medium">{lead.service_line}</span>
                            </div>
                          )}
                          {lead.geography && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Geo</span>
                              <span className="text-gray-700 font-medium">{lead.geography}</span>
                            </div>
                          )}
                          {lead.category && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Cat</span>
                              <span className="text-gray-700 font-semibold bg-yellow-100 px-1.5 py-0.5 rounded">{lead.category}</span>
                            </div>
                          )}
                          {canFilterByOwner && (lead.owner_name != null || lead.owner_id) && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Owner</span>
                              <span className="text-gray-700 font-medium">{lead.owner_name || teamUsers.find(u => u.id === lead.owner_id)?.name || '—'}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-2.5 pt-2.5 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/leads/${lead.id}`);
                            }}
                            className="w-full flex items-center justify-center space-x-1.5 px-2 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded transition-colors text-xs font-medium"
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                            <span>View</span>
                            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {stageLeads.length === 0 && (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-xs font-medium">No leads</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* Clean Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white/95 backdrop-blur-md rounded-xl shadow-2xl max-w-2xl w-full border border-white/20 fade-in">
              <div className="p-6">
                <div className="mb-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {editingLead ? 'Edit Lead' : 'New Lead'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingLead ? 'Update lead information' : 'Add a new lead to your pipeline'}
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Brand / Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-premium"
                      placeholder="Enter brand or company name (e.g. Acme Corp)"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Brand or company only — not the contact person&apos;s name.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Contact First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="input-premium"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Contact Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="input-premium"
                        placeholder="Last name (optional)"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="input-premium"
                        placeholder="name@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Contact Mobile
                      </label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        className="input-premium"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Stage <span className="text-red-500">*</span>
                      </label>
                        <select
                          value={formData.stage}
                          onChange={(e) => {
                            const newStage = e.target.value;
                            setFormData({ 
                              ...formData, 
                              stage: newStage,
                              // Clear lost_reason if stage is not 'lost'
                              lost_reason: newStage === 'lost' ? formData.lost_reason : '',
                              // Clear won_date if stage is not 'won'
                              won_date: newStage === 'won' ? formData.won_date : '',
                              // Clear lost_date if stage is not 'lost'
                              lost_date: newStage === 'lost' ? formData.lost_date : '',
                            });
                          }}
                          className="input-premium"
                        >
                        {stages.map((stage) => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Value (INR)
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                        className="input-premium w-full"
                        placeholder="0 or 0.00"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter amount in Indian Rupees (₹)
                      </p>
                      {formData.stage === 'won' && (
                        <>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5 mt-4">
                            Won Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.won_date}
                            onChange={(e) => setFormData({ ...formData, won_date: e.target.value })}
                            className="input-premium"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Date when this lead was won
                          </p>
                        </>
                      )}
                      {formData.stage === 'lost' && (
                        <>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5 mt-4">
                            Lost Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.lost_date}
                            onChange={(e) => setFormData({ ...formData, lost_date: e.target.value })}
                            className="input-premium"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Date when this lead was lost
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Win Probability (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.probability}
                        onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                        className="input-premium"
                        placeholder="0"
                      />
                      {formData.probability && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-slate-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${formData.probability}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Expected Close Date
                      </label>
                      <input
                        type="date"
                        value={formData.expected_close_date}
                        onChange={(e) =>
                          setFormData({ ...formData, expected_close_date: e.target.value })
                        }
                        className="input-premium"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Company/Account Name <span className="text-xs text-gray-500">(auto-created if not selected)</span>
                    </label>
                    <div className="flex space-x-2">
                      <select
                        value={formData.account_id}
                        onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                        className="input-premium flex-1"
                      >
                        <option value="">Auto-create account from brand name</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAccountModal(true)}
                        className="btn-secondary px-4 whitespace-nowrap"
                        title="Create new account"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        New Account
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      If no account is selected, one will be automatically created using the Brand name.
                    </p>
                  </div>
                  
                  {/* Additional Lead Information Section */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-bold text-gray-700 mb-4">Additional Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Contacts <span className="text-gray-500 font-normal">(Multiple contacts can be added)</span>
                        </label>
                        <div className="space-y-2">
                          {/* Selected Contacts Display */}
                          {selectedContactIds.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedContactIds.map((contactId) => {
                                const contact = contacts.find(c => c.id === contactId);
                                const leadContact = leadContacts.find(lc => lc.contact_id === contactId);
                                if (!contact) return null;
                                return (
                                  <div
                                    key={contactId}
                                    className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2 flex-wrap">
                                        <span className="font-medium text-gray-700">
                                          {contact.first_name} {contact.last_name}
                                        </span>
                                        {leadContact?.is_primary && (
                                          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Primary</span>
                                        )}
                                        {leadContact?.role && (
                                          <span className="text-xs text-gray-600">({leadContact.role})</span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                        {contact.title && <div>{contact.title}</div>}
                                        {contact.email && (
                                          <a href={`mailto:${contact.email}`} className="hover:text-indigo-600 block" onClick={(e) => e.stopPropagation()}>
                                            {contact.email}
                                          </a>
                                        )}
                                        {contact.phone && (
                                          <a href={`tel:${contact.phone}`} className="hover:text-indigo-600 block" onClick={(e) => e.stopPropagation()}>
                                            {contact.phone}
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedContactIds(selectedContactIds.filter(id => id !== contactId));
                                      }}
                                      className="text-red-500 hover:text-red-700 ml-1 flex-shrink-0"
                                      title="Remove contact"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Add Contact Selector - searchable */}
                          <div className="flex space-x-2" ref={contactDropdownRef}>
                            <div className="relative flex-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setContactDropdownOpen(!contactDropdownOpen);
                                  if (!contactDropdownOpen) setContactSearchTerm('');
                                }}
                                className="input-premium w-full text-left flex items-center justify-between gap-2 min-h-[42px] py-2"
                              >
                                <span className="text-gray-500 truncate">
                                  Select contact to add...
                                </span>
                                <ChevronDownIcon className={`h-5 w-5 text-gray-400 flex-shrink-0 ${contactDropdownOpen ? 'rotate-180' : ''}`} />
                              </button>
                              {contactDropdownOpen && (
                                <div className="absolute z-[100] mt-1 left-0 right-0 min-w-[280px] bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden flex flex-col max-h-72">
                                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                                    <div className="relative">
                                      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                      <input
                                        type="text"
                                        placeholder="Search by name, brand, email, phone..."
                                        value={contactSearchTerm}
                                        onChange={(e) => setContactSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                      />
                                    </div>
                                  </div>
                                  <div className="overflow-y-auto flex-1 min-h-0">
                                    {(() => {
                                      const available = contacts.filter(c => !selectedContactIds.includes(c.id));
                                      const q = contactSearchTerm.trim().toLowerCase();
                                      const filtered = q
                                        ? available.filter((c) => {
                                            const brand = c.account_id ? (accounts.find((a) => a.id === c.account_id)?.name || '') : '';
                                            return (
                                              (c.first_name || '').toLowerCase().includes(q) ||
                                              (c.last_name || '').toLowerCase().includes(q) ||
                                              (c.email || '').toLowerCase().includes(q) ||
                                              (c.phone || '').toLowerCase().includes(q) ||
                                              (c.title || '').toLowerCase().includes(q) ||
                                              brand.toLowerCase().includes(q)
                                            );
                                          })
                                        : available;
                                      if (filtered.length === 0) {
                                        return (
                                          <div className="p-4 text-center text-sm text-gray-500">
                                            {available.length === 0
                                              ? 'All contacts already added'
                                              : 'No contacts match your search'}
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="p-1">
                                          {filtered.map((contact) => {
                                            const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email || 'Unknown';
                                            const brand = contact.account_id ? accounts.find((a) => a.id === contact.account_id)?.name : null;
                                            return (
                                              <button
                                                key={contact.id}
                                                type="button"
                                                onClick={() => {
                                                  setSelectedContactIds([...selectedContactIds, contact.id]);
                                                  setContactSearchTerm('');
                                                }}
                                                className="w-full flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-left text-sm hover:bg-indigo-50 transition-colors"
                                              >
                                                <span className="font-medium text-gray-900">{name}</span>
                                                <span className="text-xs text-gray-500 truncate w-full">
                                                  {[brand, contact.title, contact.email].filter(Boolean).join(' • ')}
                                                </span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowContactModal(true)}
                              className="btn-secondary px-4 whitespace-nowrap"
                              title="Create new contact"
                            >
                              <PlusIcon className="h-4 w-4 mr-1" />
                              New Contact
                            </button>
                          </div>
                          
                          {/* Primary Contact Indicator */}
                          {selectedContactIds.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              💡 The first contact added will be set as primary. You can change this after saving.
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Service Line
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setServiceLineDropdownOpen(!serviceLineDropdownOpen)}
                            className="input-premium w-full text-left flex items-center justify-between gap-2 min-h-[42px] py-2"
                          >
                            <span className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
                              {(() => {
                                const val = (formData.service_line || '').trim();
                                const parts = val ? val.split(',').map(s => s.trim()).filter(Boolean) : [];
                                if (parts.length === 0) {
                                  return <span className="text-gray-500">Select service lines...</span>;
                                }
                                const maxVisible = 4;
                                const visible = parts.slice(0, maxVisible);
                                const rest = parts.length - maxVisible;
                                return (
                                  <>
                                    {visible.map((p) => (
                                      <span key={p} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                                        {p}
                                      </span>
                                    ))}
                                    {rest > 0 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                        +{rest} more
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </span>
                            <ChevronDownIcon className={`h-5 w-5 text-gray-400 flex-shrink-0 ${serviceLineDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {serviceLineDropdownOpen && (
                            <>
                              <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg py-1 max-h-60 overflow-auto" role="listbox">
                                {allServiceLines.map((option) => {
                                  const currentStr = formData.service_line || '';
                                  const selected = currentStr.split(',').map(s => s.trim()).filter(Boolean).includes(option);
                                  return (
                                    <button
                                      key={option}
                                      type="button"
                                      role="option"
                                      aria-selected={selected}
                                      onClick={() => {
                                        const current = currentStr.split(',').map(s => s.trim()).filter(Boolean);
                                        const next = selected ? current.filter(x => x !== option) : [...current, option];
                                        setFormData({ ...formData, service_line: next.join(', ') });
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50"
                                    >
                                      <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                        {selected && <CheckIcon className="h-3 w-3 text-white" />}
                                      </span>
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="fixed inset-0 z-10" aria-hidden onClick={() => setServiceLineDropdownOpen(false)} />
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Industry
                        </label>
                        <input
                          type="text"
                          value={formData.industry}
                          onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                          className="input-premium"
                          placeholder="e.g., Technology, Healthcare"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Brand Type
                        </label>
                        <input
                          type="text"
                          value={formData.brand_type}
                          onChange={(e) => setFormData({ ...formData, brand_type: e.target.value })}
                          className="input-premium"
                          placeholder="e.g., B2B, B2C"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Geography
                        </label>
                        <input
                          type="text"
                          value={formData.geography}
                          onChange={(e) => setFormData({ ...formData, geography: e.target.value })}
                          className="input-premium"
                          placeholder="e.g., North America, APAC"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Lead Source
                        </label>
                        <select
                          value={formData.lead_source}
                          onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}
                          className="input-premium"
                        >
                          <option value="">Select source (optional)</option>
                          {LEAD_SOURCE_OPTIONS.map((source) => (
                            <option key={source} value={source}>
                              {source}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                          className="input-premium"
                        >
                          <option value="">Select category (optional)</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Urgency Score (1-20)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={formData.urgency_score}
                          onChange={(e) => setFormData({ ...formData, urgency_score: e.target.value })}
                          className="input-premium"
                          placeholder="1-20"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Rate urgency from 1 (low) to 20 (critical)
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Date Created
                        </label>
                        <input
                          type="date"
                          value={formData.date_created}
                          onChange={(e) => setFormData({ ...formData, date_created: e.target.value })}
                          className="input-premium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Last Contacted At
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.last_contacted_at}
                          onChange={(e) => setFormData({ ...formData, last_contacted_at: e.target.value })}
                          className="input-premium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Next Action
                        </label>
                        <input
                          type="text"
                          value={formData.next_action}
                          onChange={(e) => setFormData({ ...formData, next_action: e.target.value })}
                          className="input-premium"
                          placeholder="e.g., Follow up call"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Next Action Date
                        </label>
                        <input
                          type="date"
                          value={formData.next_action_date}
                          onChange={(e) => setFormData({ ...formData, next_action_date: e.target.value })}
                          className="input-premium"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Brand
                        </label>
                        <input
                          type="text"
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          className="input-premium"
                          placeholder="Brand name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Decision Maker
                        </label>
                        <input
                          type="text"
                          value={formData.decision_maker}
                          onChange={(e) => setFormData({ ...formData, decision_maker: e.target.value })}
                          className="input-premium"
                          placeholder="Decision maker name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Decision Maker Contact
                        </label>
                        <select
                          value={formData.decision_maker_contact_id}
                          onChange={(e) => setFormData({ ...formData, decision_maker_contact_id: e.target.value })}
                          className="input-premium"
                        >
                          <option value="">Select contact (optional)</option>
                          {contacts.map((contact) => {
                            const contactParts = [
                              contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : contact.first_name || contact.last_name,
                              contact.title,
                              contact.email,
                              contact.phone,
                            ].filter(Boolean);
                            return (
                              <option key={contact.id} value={contact.id}>
                                {contactParts.join(' • ')}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Decision Maker Level
                        </label>
                        <select
                          value={formData.decision_maker_level}
                          onChange={(e) => setFormData({ ...formData, decision_maker_level: e.target.value })}
                          className="input-premium"
                        >
                          <option value="">Select level (optional)</option>
                          <option value="C-Level">C-Level</option>
                          <option value="VP">VP</option>
                          <option value="Director">Director</option>
                          <option value="Manager">Manager</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Influencers
                        </label>
                        <textarea
                          value={formData.influencers}
                          onChange={(e) => setFormData({ ...formData, influencers: e.target.value })}
                          className="input-premium"
                          rows={2}
                          placeholder="List key influencers (one per line or comma-separated)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Reason
                        </label>
                        <textarea
                          value={formData.reason}
                          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                          className="input-premium"
                          rows={2}
                          placeholder="General reason or context for this lead"
                        />
                      </div>
                    </div>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Problem Statement
                        </label>
                        <textarea
                          value={formData.problem_statement}
                          onChange={(e) => setFormData({ ...formData, problem_statement: e.target.value })}
                          className="input-premium"
                          rows={3}
                          placeholder="Describe the problem the client is facing..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Solution Positioning
                        </label>
                        <textarea
                          value={formData.solution_positioning}
                          onChange={(e) => setFormData({ ...formData, solution_positioning: e.target.value })}
                          className="input-premium"
                          rows={3}
                          placeholder="How we position our solution..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Competitor
                        </label>
                        <input
                          type="text"
                          value={formData.competitor}
                          onChange={(e) => setFormData({ ...formData, competitor: e.target.value })}
                          className="input-premium"
                          placeholder="Main competitor name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Notes
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          className="input-premium"
                          rows={3}
                          placeholder="Additional notes..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Close Plan Created
                        </label>
                        <select
                          value={formData.close_plan_id ? 'Yes' : 'No'}
                          onChange={(e) => {
                            // This is a simplified logic. Ideally, we would create/link a plan.
                            // For now, we assume 'Yes' means there is a plan ID (handled elsewhere or dummy)
                            // and 'No' clears it.
                            if (e.target.value === 'No') {
                              setFormData({ ...formData, close_plan_id: '' });
                            }
                            // 'Yes' logic would typically involve creating a plan or selecting one.
                          }}
                          className="input-premium"
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                      </div>
                      {formData.stage === 'lost' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Lost Reason <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={formData.lost_reason}
                            onChange={(e) => setFormData({ ...formData, lost_reason: e.target.value })}
                            className="input-premium"
                            placeholder="Enter the reason why this lead was lost..."
                            rows={3}
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Please provide details about why this deal was lost
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <TagSelector
                    selectedTagIds={selectedTagIds}
                    onChange={setSelectedTagIds}
                    entityType="lead"
                    entityId={editingLead?.id}
                  />
                  <CustomFieldsRenderer
                    entityType="lead"
                    entityId={editingLead?.id}
                    values={customFieldValues}
                    onChange={(fieldName, value) => {
                      setCustomFieldValues(prev => ({
                        ...prev,
                        [fieldName]: value,
                      }));
                    }}
                  />
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? 'Saving...'
                        : editingLead
                        ? 'Update Lead'
                        : 'Create Lead'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingLead(null);
                        resetForm();
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Create Account Modal */}
        {showAccountModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white/95 backdrop-blur-md rounded-xl shadow-2xl max-w-lg w-full border border-white/20 fade-in">
              <div className="p-6">
                <div className="mb-5">
                  <h3 className="text-xl font-bold text-gray-900">Create New Account</h3>
                  <p className="text-sm text-gray-500 mt-1">Add a new account to your database</p>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (newAccountData.name.trim()) {
                      createAccountMutation.mutate(newAccountData);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Account Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={newAccountData.name}
                      onChange={(e) => setNewAccountData({ ...newAccountData, name: e.target.value })}
                      className="input-premium"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={newAccountData.industry}
                      onChange={(e) => setNewAccountData({ ...newAccountData, industry: e.target.value })}
                      className="input-premium"
                      placeholder="e.g., Technology, Healthcare"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Website
                    </label>
                    <input
                      type="url"
                      value={newAccountData.website}
                      onChange={(e) => setNewAccountData({ ...newAccountData, website: e.target.value })}
                      className="input-premium"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={newAccountData.phone}
                      onChange={(e) => setNewAccountData({ ...newAccountData, phone: e.target.value })}
                      className="input-premium"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Address
                    </label>
                    <textarea
                      value={newAccountData.address}
                      onChange={(e) => setNewAccountData({ ...newAccountData, address: e.target.value })}
                      className="input-premium"
                      rows={3}
                      placeholder="Street address, City, State, ZIP"
                    />
                  </div>
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={createAccountMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createAccountMutation.isPending ? 'Creating...' : 'Create Account'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAccountModal(false);
                        setNewAccountData({ name: '', industry: '', website: '', phone: '', address: '' });
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Create Contact Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white/95 backdrop-blur-md rounded-xl shadow-2xl max-w-lg w-full border border-white/20 fade-in">
              <div className="p-6">
                <div className="mb-5">
                  <h3 className="text-xl font-bold text-gray-900">Create New Contact</h3>
                  <p className="text-sm text-gray-500 mt-1">Add a new contact to your database</p>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (newContactData.first_name.trim()) {
                      createContactMutation.mutate({
                        ...newContactData,
                        account_id: formData.account_id ? parseInt(formData.account_id) : undefined,
                      });
                    }
                  }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={newContactData.first_name}
                        onChange={(e) => setNewContactData({ ...newContactData, first_name: e.target.value })}
                        className="input-premium"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={newContactData.last_name}
                        onChange={(e) => setNewContactData({ ...newContactData, last_name: e.target.value })}
                        className="input-premium"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newContactData.email}
                      onChange={(e) => setNewContactData({ ...newContactData, email: e.target.value })}
                      className="input-premium"
                      placeholder="john.doe@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={newContactData.phone}
                      onChange={(e) => setNewContactData({ ...newContactData, phone: e.target.value })}
                      className="input-premium"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Title
                    </label>
                    <input
                      type="text"
                      value={newContactData.title}
                      onChange={(e) => setNewContactData({ ...newContactData, title: e.target.value })}
                      className="input-premium"
                      placeholder="e.g., CEO, Manager, Developer"
                    />
                  </div>
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={createContactMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createContactMutation.isPending ? 'Creating...' : 'Create Contact'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowContactModal(false);
                        setNewContactData({ first_name: '', last_name: '', email: '', phone: '', title: '' });
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Leads;
